import React, { useMemo } from "react";
import { Box, Text, Flex, Stack, Image, Badge } from "@chakra-ui/react";
import colors from "../../helpers/colors";
import ProfileImage from "../common/ProfileImage";
import myaImg from "../../assets/img/mya.png";

// Survey shapes (mirrors src/components/AIAgent/Survey/types.ts in the
// mobile app). We avoid importing from there to keep the web bundle clean
// from RN dependencies.
//
// Question content (assistant, metadata.kind === "question"):
//   { id, criterionId, prompt, type: "yes_no" | "multiple_choice" |
//     "open_text" | "certificate_upload", options?, multi?, placeholder?,
//     allowClaimOnly? }
//
// Answer content (candidate, metadata.kind === "answer"):
//   { kind: "answer", questionId, value:
//       boolean              // yes_no
//     | string[] | string    // multiple_choice
//     | string               // open_text
//     // certificate_upload variants:
//     | { fileId: string }                              // legacy uploaded
//     | { claimOnly: true }                             // claimed only
//     | { doesNotHave: true }                           // candidate says no
//     | { certificationId, certificationName, issue?, expiry? }  // picker v2
//     | { certificationName, issue?, expiry?, isOther: true }    // picker v2 "Other"
//   }
//
// Survey result (assistant, metadata.kind === "survey_result"):
//   { verdict: "Pass" | "Fail", summary: string,
//     decisionSource: "Deterministic" | "LLMTiebreaker" }
//
// Anything else is treated as a legacy chat-mode message and rendered as
// free text.

function safeJsonParse(s) {
  // Worker currently emits JSON strings, but a future aggregator change
  // (JSON column, server-side pre-parse) could hand us an object directly.
  // Guard against that so we don't regress to rendering "[object Object]".
  if (typeof s === "object" && s !== null) return s;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// Map the raw question type enum to a human-friendly label for screen
// readers / recruiters. Falls back to the raw enum if unknown.
const QUESTION_TYPE_LABELS = {
  yes_no: "Yes / No",
  multiple_choice: "Multiple choice",
  open_text: "Free text",
  certificate_upload: "Certificate",
};

// Parse the survey-mode cert date payload as a timezone-agnostic
// `YYYY-MM-DD` string and re-emit as `DD/MM/YYYY` for the recruiter view.
// The mobile picker (moblyze-mobile#120) was updated to emit date-only
// strings using the candidate's local-time components, so what the
// recruiter sees is what the candidate picked regardless of either
// party's UTC offset. We still accept full ISO datetimes for backward
// compat with any pre-fix transcript rows in production — but parse the
// date portion explicitly rather than via `new Date()` + UTC accessors,
// which off-by-ones at month/year boundaries when the candidate is east
// of UTC and the wire payload is `toISOString()`-shaped.
function formatCertDate(value) {
  if (typeof value !== "string" || value.length < 10) return null;
  // Match the leading YYYY-MM-DD portion of either a date-only string
  // ("2024-01-15") or an ISO datetime ("2024-01-15T00:00:00.000Z").
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, yyyy, mm, dd] = m;
  // Sanity-check the calendar values; reject 2024-13-40 etc.
  const month = Number(mm);
  const day = Number(dd);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${dd}/${mm}/${yyyy}`;
}

function describeAnswerValue(value) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.length === 0) return "(no answer)";
    return value.join(", ");
  }
  if (typeof value === "string") {
    if (value.length === 0) return "(no answer)";
    return value;
  }
  if (value && typeof value === "object") {
    // Order matters: explicit escape hatches win over upload metadata so a
    // candidate's clear "claim only" / "no" can't be masked by leftover
    // picker fields that happen to be in the payload.
    if (value.claimOnly) return "I have this but didn't upload";
    if (value.doesNotHave) return "Doesn't have this";

    // Cert upload v2 — the survey-mode picker writes one of these payload
    // shapes. Surface the cert name + dates so the recruiter sees what was
    // actually uploaded rather than a bare file-id ellipsis.
    if (value.certificationName || value.isOther) {
      const name = value.certificationName || "(unnamed certificate)";
      const labelPrefix = value.isOther ? "Other — " : "";
      const issued = formatCertDate(value.issue);
      const expires = formatCertDate(value.expiry);
      const datesParts = [];
      if (issued) datesParts.push(`issued ${issued}`);
      if (expires) datesParts.push(`expires ${expires}`);
      const dates = datesParts.length > 0 ? ` (${datesParts.join(", ")})` : "";
      return `${labelPrefix}${name}${dates}`;
    }

    if (value.fileId) return `Uploaded file (${value.fileId.slice(0, 8)}…)`;
  }
  return JSON.stringify(value);
}

function QuestionBubble({ content }) {
  const q = safeJsonParse(content);
  if (!q || !q.prompt) {
    return <PlainAssistantText text={content} />;
  }
  return (
    <Box maxWidth="80%">
      <Text
        fontSize="sm"
        color="white"
        lineHeight="1.5"
        whiteSpace="pre-wrap"
        p="6px 12px"
      >
        {q.prompt}
      </Text>
      <Badge ml="12px" mt="2px" colorScheme="teal" fontSize="9px">
        {QUESTION_TYPE_LABELS[q.type] || q.type}
      </Badge>
    </Box>
  );
}

function PlainAssistantText({ text }) {
  return (
    <Box maxWidth="80%">
      <Text
        fontSize="sm"
        color="white"
        lineHeight="1.5"
        whiteSpace="pre-wrap"
        p="6px 12px"
      >
        {text}
      </Text>
    </Box>
  );
}

function AnswerBubble({ content }) {
  const a = safeJsonParse(content);
  // Guard the `in` check: safeJsonParse can return a primitive (a bare
  // `true`/number/string answer serialised on its own), and `"value" in
  // <primitive>` throws a TypeError. Only object payloads carry `value`.
  const human =
    a && typeof a === "object" && "value" in a
      ? describeAnswerValue(a.value)
      : content;
  return (
    <Box maxWidth="80%">
      <Text
        fontSize="sm"
        color="white"
        lineHeight="1.5"
        whiteSpace="pre-wrap"
        bgColor="teal.900"
        borderRadius="12px"
        p="6px 12px"
      >
        {human}
      </Text>
    </Box>
  );
}

function ResultCard({ content }) {
  const r = safeJsonParse(content) || {};
  const passed = r.verdict === "Pass";
  return (
    <Box
      width="100%"
      mt="8px"
      p="16px"
      borderRadius="12px"
      borderWidth="1px"
      borderColor={passed ? "green.400" : "orange.400"}
      backgroundColor="blackAlpha.500"
    >
      <Flex align="center" mb="8px">
        <Badge
          colorScheme={passed ? "green" : "orange"}
          fontSize="xs"
          mr="8px"
        >
          {r.verdict || "—"}
        </Badge>
        {r.decisionSource && (
          <Text fontSize="xs" color="gray.400">
            {r.decisionSource === "Deterministic"
              ? "Deterministic check"
              : "LLM tiebreaker"}
          </Text>
        )}
      </Flex>
      {r.summary && (
        <Text fontSize="sm" color="white" lineHeight="1.5" whiteSpace="pre-line">
          {r.summary}
        </Text>
      )}
    </Box>
  );
}

const MyaConversationSection = ({ messages, candidate }) => {
  // CandidateDetails also threads a `parsedMetadata` prop here; it's unused
  // so we simply don't destructure it — React ignores extra props.
  const sortedMessages = useMemo(() => {
    if (!Array.isArray(messages)) return [];
    return [...messages].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      if (aTime !== bTime) return aTime - bTime;
      // Stable tiebreak: secondary sort by displayOrder then id. Pairs
      // with the worker's same fix in getAggregatedScreeningConversation.
      const aOrder = a.displayOrder ?? 0;
      const bOrder = b.displayOrder ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aId = a.id ?? "";
      const bId = b.id ?? "";
      if (aId < bId) return -1;
      if (aId > bId) return 1;
      return 0;
    });
  }, [messages]);

  if (!messages || messages.length === 0) {
    return (
      <Box backgroundColor={colors.black} borderRadius="12px" p="24px">
        <Text fontSize="xl" fontWeight="bold" color="white" mb="16px">
          MYA Conversation
        </Text>
        <Text fontSize="sm" color="gray.400">
          No conversation messages available.
        </Text>
      </Box>
    );
  }

  return (
    <Box p="12px">
      <Text fontSize="xl" fontWeight="bold" color="white" mb="24px">
        MYA Conversation
      </Text>

      <Stack spacing="20px">
        {sortedMessages.map((message) => {
          const kind = message.metadata?.kind;
          const isAssistant = message.role === "assistant";

          // Section divider between the must-haves and the nice-to-haves.
          // Emitted by the worker when the survey crosses into the optional
          // phase so the recruiter can tell where the gating questions
          // ended and the colour follow-ups began.
          if (kind === "phase_transition") {
            return (
              <Flex
                key={message.id}
                direction="row"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  flex="1"
                  height="1px"
                  bg="gray.700"
                  maxWidth="80px"
                  mr="12px"
                />
                <Text
                  fontSize="xs"
                  color="gray.400"
                  fontStyle="italic"
                  textAlign="center"
                >
                  {message.content}
                </Text>
                <Box
                  flex="1"
                  height="1px"
                  bg="gray.700"
                  maxWidth="80px"
                  ml="12px"
                />
              </Flex>
            );
          }

          // Centered, full-width result card.
          if (kind === "survey_result") {
            return (
              <Flex
                key={message.id}
                direction="row"
                alignItems="flex-end"
                justifyContent="flex-start"
              >
                <Box mr="12px" flexShrink={0}>
                  <Image src={myaImg} alt="MYA" width="24px" height="24px" borderRadius="50%" />
                </Box>
                <ResultCard content={message.content} />
              </Flex>
            );
          }

          return (
            <Flex
              key={message.id}
              direction="row"
              alignItems="flex-end"
              justifyContent={isAssistant ? "flex-start" : "flex-end"}
            >
              {isAssistant && (
                <>
                  <Box mr="12px" flexShrink={0}>
                    <Image src={myaImg} alt="MYA" width="24px" height="24px" borderRadius="50%" />
                  </Box>
                  {kind === "question" ? (
                    <QuestionBubble content={message.content} />
                  ) : (
                    <PlainAssistantText text={message.content} />
                  )}
                </>
              )}

              {!isAssistant && (
                <>
                  {kind === "answer" ? (
                    <AnswerBubble content={message.content} />
                  ) : (
                    <Box maxWidth="80%">
                      <Text
                        fontSize="sm"
                        color="white"
                        lineHeight="1.5"
                        whiteSpace="pre-wrap"
                        bgColor="teal.900"
                        borderRadius="12px"
                        p="6px 12px"
                      >
                        {message.content}
                      </Text>
                    </Box>
                  )}
                  <Box ml="12px" flexShrink={0}>
                    <ProfileImage
                      imageUrl={candidate?.profileImageUrl}
                      name={candidate?.fullName}
                      size="24px"
                    />
                  </Box>
                </>
              )}
            </Flex>
          );
        })}
      </Stack>

      <Box mt="20px" pt="16px" borderTop="1px solid" borderColor="gray.700">
        <Text fontSize="xs" color="gray.400" textAlign="center">
          Follow-up completed
        </Text>
      </Box>
    </Box>
  );
};

export default MyaConversationSection;
